/**
 * The submit flow behind the reusable invite link (`/onboard/:advisorId`, AdvisorPublicInvite).
 *
 * Steps:
 * - `details`: name and email. Submitting POSTs `/public/client-invite`. A new email gets a
 *   redirect into its intake form straight away.
 * - `choice`: the server answered `requiresChoice` because this email already has a client
 *   under the advisor. The visitor picks continue / start over / someone else, and the same
 *   endpoint is called again carrying that choice.
 * - `signIn`: any choice while signed out, because an email that already has an assessment
 *   always signs in. The server returns a sign-in link whose `redirect_to` opens the profile
 *   list ("continue") or the new assessment's form ("start over" / "someone else"); the page
 *   shows it as a button. A signed-in visitor skips this step: the same link forwards them on.
 * - `starting`: "start over" / "someone else" while signed in. The server created a new client
 *   on the same email and we are navigating into its form. Each visit to the choice step gets a
 *   fresh `inviteRequestId`; a retried request carrying the same id (lost response, second click)
 *   gets the client it already created rather than a duplicate.
 *
 * A signed-in visitor's email is fixed to their account email, because `/onboarding-form`
 * only opens a form whose email matches the session's.
 *
 * When the advisor requires consumer SMS MFA the details step also takes a phone number, sent
 * with every request as `consumerSmsMfa`. The server enrols it only for a new email; a returning
 * client verifies their phone by SMS after signing in.
 *
 * The request itself is `publicInviteApi.postClientInvite`, which gives up after 30 seconds.
 * Trying again is safe: a retried details submission finds the client the first attempt created
 * and asks the choice, and a retried choice carries the same `inviteRequestId`.
 *
 * Every full-page navigation sets `isRedirecting`, keeping the buttons disabled until the browser
 * leaves. If the browser restores this page from its back/forward cache (the visitor pressed Back
 * after being sent on), the flow returns to the details step with the typed values kept. It does
 * not return to the choice, because a second "start new assessment" there would quietly create a
 * duplicate client.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { isValidEmailFormat, newUuid, ReturningIntakeChoice } from '@shared';
import { publicInviteApi, type ClientInviteRequest } from '@client/api/publicInviteApi';
import {
  formatByCountry,
  stripDigits,
  validateForCountry,
  type SupportedCountryCode,
} from '@client/util/clerkAuthHelpers';

export type PublicInviteStep = 'details' | 'choice' | 'signIn' | 'starting';

interface PublicInviteFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneCountryCode: SupportedCountryCode;
  phoneNumber: string;
}

type PublicInviteFormErrors = Partial<
  Record<'firstName' | 'lastName' | 'email' | 'phoneNumber', string>
>;

export function usePublicInviteFlow({
  advisorId,
  signedInEmail,
  consumerSmsMfaRequired,
}: {
  advisorId: string | undefined;
  signedInEmail: string | null;
  consumerSmsMfaRequired: boolean;
}) {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<PublicInviteFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneCountryCode: '+1',
    phoneNumber: '',
  });
  const [formErrors, setFormErrors] = useState<PublicInviteFormErrors>({});
  const [isPhoneTouched, setIsPhoneTouched] = useState(false);
  const [step, setStep] = useState<PublicInviteStep>('details');
  const [choice, setChoice] = useState<ReturningIntakeChoice | null>(null);
  const [signInUrl, setSignInUrl] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [inviteRequestId, setInviteRequestId] = useState<string | null>(null);

  const mutation = useMutation({ mutationFn: publicInviteApi.postClientInvite });

  useEffect(() => {
    function handlePageShow(event: PageTransitionEvent) {
      if (!event.persisted) {
        return;
      }
      setIsRedirecting(false);
      setChoice(null);
      setSignInUrl(null);
      setStep('details');
    }

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  function redirectTo(url: string) {
    setIsRedirecting(true);
    window.location.href = url;
  }

  const email = signedInEmail ?? formData.email;
  const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();

  function phoneNumberError(): string | null {
    return validateForCountry(stripDigits(formData.phoneNumber), formData.phoneCountryCode);
  }

  const isFormComplete =
    !consumerSmsMfaRequired ||
    (formData.firstName.trim().length > 0 &&
      formData.lastName.trim().length > 0 &&
      isValidEmailFormat(email.trim()) &&
      phoneNumberError() === null);

  function validateForm(): boolean {
    const errors: PublicInviteFormErrors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!signedInEmail) {
      if (!formData.email.trim()) {
        errors.email = 'Email is required';
      } else if (!isValidEmailFormat(formData.email.trim())) {
        errors.email = 'Please enter a valid email address';
      }
    }
    if (consumerSmsMfaRequired) {
      const phoneError = phoneNumberError();
      if (phoneError) {
        errors.phoneNumber = phoneError;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function buildBody(
    inviteAdvisorId: string,
    returningChoice?: ReturningIntakeChoice,
  ): ClientInviteRequest {
    const body: ClientInviteRequest = {
      advisorId: inviteAdvisorId,
      clientFirstName: formData.firstName.trim(),
      clientLastName: formData.lastName.trim(),
      clientEmail: email.trim().toLowerCase(),
    };

    const hasAccessToResults = searchParams.get('hasAccessToResults');
    if (hasAccessToResults === 'true' || hasAccessToResults === 'false') {
      body.hasAccessToResults = hasAccessToResults === 'true';
    }
    const clientRelationshipStage = searchParams.get('clientRelationshipStage');
    if (clientRelationshipStage) {
      body.clientRelationshipStage = clientRelationshipStage;
    }
    const customReferralSourceId = searchParams.get('customReferralSourceId');
    if (customReferralSourceId) {
      body.customReferralSourceId = customReferralSourceId;
    }
    if (consumerSmsMfaRequired) {
      body.consumerSmsMfa = {
        phoneNumber: `${formData.phoneCountryCode}${stripDigits(formData.phoneNumber)}`,
      };
    }
    if (returningChoice) {
      body.returningChoice = returningChoice;
      if (inviteRequestId) {
        body.inviteRequestId = inviteRequestId;
      }
    }

    return body;
  }

  function submitDetails(event: FormEvent) {
    event.preventDefault();
    if (!validateForm() || !advisorId) {
      return;
    }

    mutation.mutate(buildBody(advisorId), {
      onSuccess: data => {
        if (data.requiresChoice) {
          setChoice(null);
          setInviteRequestId(newUuid());
          setStep('choice');
          return;
        }
        redirectTo(data.redirectUrl!);
      },
    });
  }

  function confirmChoice() {
    if (!choice || !advisorId) {
      return;
    }

    mutation.mutate(buildBody(advisorId, choice), {
      onSuccess: data => {
        if (data.requiresSignIn && !signedInEmail) {
          setSignInUrl(data.redirectUrl!);
          setStep('signIn');
          return;
        }
        if (choice !== ReturningIntakeChoice.CONTINUE) {
          setStep('starting');
        }
        redirectTo(data.redirectUrl!);
      },
    });
  }

  function handleInputChange(field: 'firstName' | 'lastName' | 'email', value: string) {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }

  function changePhoneCountryCode(countryCode: SupportedCountryCode) {
    setFormData(current => ({
      ...current,
      phoneCountryCode: countryCode,
      phoneNumber: formatByCountry(stripDigits(current.phoneNumber), countryCode),
    }));
    setFormErrors(current => ({ ...current, phoneNumber: undefined }));
  }

  function changePhoneNumber(value: string) {
    setFormData(current => ({
      ...current,
      phoneNumber: formatByCountry(stripDigits(value), current.phoneCountryCode),
    }));
    setFormErrors(current => ({ ...current, phoneNumber: undefined }));
  }

  function blurPhoneNumber() {
    setIsPhoneTouched(true);
    setFormErrors(current => ({ ...current, phoneNumber: phoneNumberError() ?? undefined }));
  }

  function backToDetails() {
    mutation.reset();
    setChoice(null);
    setSignInUrl(null);
    setStep('details');
  }

  function backToChoice() {
    mutation.reset();
    setSignInUrl(null);
    setStep('choice');
  }

  return {
    step,
    formData,
    formErrors,
    isFormComplete,
    isPhoneTouched,
    email,
    fullName,
    choice,
    setChoice,
    signInUrl,
    isSubmitting: mutation.isPending || isRedirecting,
    submitError: mutation.error?.message ?? null,
    handleInputChange,
    changePhoneCountryCode,
    changePhoneNumber,
    blurPhoneNumber,
    submitDetails,
    confirmChoice,
    backToDetails,
    backToChoice,
  };
}
